import { Maybe, RemoteData, maybe, rd } from '@passionware/monads';
import {
  CustomFieldDefinition,
  CustomFieldGroupDefinition,
  MetastructureField,
  MetastructureFieldType
} from '__types__';
import { get, keyBy } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import store from 'store';
import { orderReducer } from 'v1/helpers/misc';
import type { ResourceTypeWorkspaceBinding } from 'v5/api/resource-types/resource-type-bindings.api';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import { isResourceTypeEnabledInWorkspace } from 'v5/services/internal/ResourceTypeBindingsService/mergeWorkspaceMetastructureFields';
import type { ResourceTypeBindingsService } from 'v5/services/internal/ResourceTypeBindingsService/ResourceTypeBindingsService';
import { waitForReduxState } from 'v5/platform/lang/reduxWaitFor';
import {
  getCustomFieldDefinitions,
  getCustomFieldGroupDefinitions
} from 'store/v1/custom_fields/custom_fields.selectors';
import {
  FieldNameTypeMapping,
  MetastructureMappingService,
  MetastructureRequest,
  ResolvedMetastructureField
} from './MetastructureMappingService';
import {
  mergedWorkspaceMetastructureForRequest,
  withWorkspaceOverlay
} from './workspaceMetastructureOverlay';

/**
 * Helper function to get custom field definitions and group definitions
 */
function useCustomFieldDefinitionsAndGroups() {
  const customFieldDefinitions = keyBy(
    useSelector(getCustomFieldDefinitions),
    'id'
  );
  const customFieldGroupDefinitions = keyBy(
    useSelector(getCustomFieldGroupDefinitions),
    'id'
  );

  return { customFieldDefinitions, customFieldGroupDefinitions };
}

/**
 * Helper function to extract metastructure fields from state based on a request.
 * Workspace overlays are applied by callers once bindings are loaded — do not
 * read bindings from a Redux selector (cache won't invalidate the selector).
 */
function extractMetastructureFields(
  state: any,
  request: MetastructureRequest
): MetastructureField[] {
  switch (request.type) {
    case 'resource': {
      const resourceType = get(state, [
        'resource_types',
        'data',
        request.subtypeId
      ]);
      return get(resourceType, ['metastructure', 'fields'], []).filter(
        (field: MetastructureField) => field.active
      );
    }
    case 'production': {
      const productionType = request.subtypeId
        ? Object.values(state.production_types?.data || {}).find(
            (type: any) => type.id === request.subtypeId
          )
        : null;
      const fields = get(productionType, ['metastructure', 'fields'], []);
      return fields.filter((field: MetastructureField) => field.active);
    }
    case 'booking': {
      const bookingType = request.subtypeId
        ? orderReducer(state.booking_types || {}).find(
            (type: any) => type.id === request.subtypeId
          )
        : null;
      const fields = get(bookingType, ['metastructure', 'fields'], []);
      return fields.filter((field: MetastructureField) => field.active);
    }
    default:
      return [];
  }
}

function resourceWorkspaceId(
  request: Maybe<{ type: string; scope: MetastructureRequest['scope'] }>
): Maybe<number> {
  return maybe.map(request, r =>
    r.type === 'resource'
      ? appScopeUtils.getWorkspaceId(r.scope)
      : maybe.ofAbsent()
  );
}

/**
 * Create a mapping of field names (usually present in search filter structures, like custom_1, custom_2, etc.) to custom field types
 * @param customFieldDefinitions - All custom field definitions
 * @returns A mapping of field names to custom field types
 */
function createFieldNameTypeMapping(
  customFieldDefinitions: Record<number, CustomFieldDefinition> = {}
): FieldNameTypeMapping {
  const mapping: FieldNameTypeMapping = {};

  Object.values(customFieldDefinitions).forEach(customFieldDef => {
    mapping[`custom_${customFieldDef.id}`] = customFieldDef.data_type;
  });

  return mapping;
}

/**
 * Resolves metastructure fields with their corresponding definitions
 */
function resolveMetastructureFields(
  fields: MetastructureField[],
  customFieldDefinitions: Record<number, CustomFieldDefinition> = {},
  customFieldGroupDefinitions: Record<number, CustomFieldGroupDefinition> = {}
): ResolvedMetastructureField[] {
  return fields.map(field => {
    switch (field.type) {
      case MetastructureFieldType.Core:
        return {
          ...field
        } as ResolvedMetastructureField;

      case MetastructureFieldType.Custom: {
        const customDef =
          customFieldDefinitions[field.custom_field_definition_id];
        if (customDef) {
          return {
            ...field,
            customFieldDefinition: customDef
          } as ResolvedMetastructureField;
        }
        // If definition not found, return with minimal info
        return {
          ...field,
          customFieldDefinition: {} as CustomFieldDefinition // This should ideally not happen
        } as ResolvedMetastructureField;
      }

      case MetastructureFieldType.CustomGroup: {
        const groupDef =
          customFieldGroupDefinitions[field.custom_fieldgroup_definition_id];
        if (groupDef) {
          return {
            ...field,
            customFieldGroupDefinition: groupDef
          } as ResolvedMetastructureField;
        }
        // If definition not found, return with minimal info
        return {
          ...field,
          customFieldGroupDefinition: {} as CustomFieldGroupDefinition // This should ideally not happen
        } as ResolvedMetastructureField;
      }

      default:
        // Fallback - should not happen with proper typing
        return field as any;
    }
  });
}

function useEmptyResourceTypeBindings(
  organizationId: Maybe<string>
): RemoteData<ResourceTypeWorkspaceBinding[]> {
  return maybe.isAbsent(organizationId) ? rd.ofIdle() : rd.of([]);
}

export function createMetastructureMappingService(config?: {
  resourceTypeBindingsService?: ResourceTypeBindingsService;
}): MetastructureMappingService {
  const resourceTypeBindingsService = config?.resourceTypeBindingsService;
  // Always call the same hook identity (never ternary around useBindings).
  const useBindings =
    resourceTypeBindingsService?.useBindings ?? useEmptyResourceTypeBindings;

  return {
    async getAllFieldNameTypeMappings(): Promise<FieldNameTypeMapping> {
      const state: any = await waitForReduxState(store, state =>
        maybe.isPresent(getCustomFieldDefinitions(state))
      );

      const customFieldDefinitions = keyBy(
        getCustomFieldDefinitions(state) || [],
        'id'
      );

      // Create field mappings from all custom field definitions
      return createFieldNameTypeMapping(customFieldDefinitions);
    },

    useCustomFieldDefinitions(): RemoteData<Record<
      number,
      CustomFieldDefinition
    > | null> {
      const customFieldDefinitions = keyBy(
        useSelector(getCustomFieldDefinitions) as
          | CustomFieldDefinition[]
          | null,
        'id'
      );
      return useMemo(
        () => rd.of(customFieldDefinitions),
        [customFieldDefinitions]
      );
    },

    async ensureCustomFieldDefinitions(): Promise<
      Record<number, CustomFieldDefinition>
    > {
      const state: any = await waitForReduxState(store, (state: any) => {
        // Wait for auth settings to be loaded (custom field definitions are part of auth.settings)
        return state.auth?.settings?.custom_field_definitions;
      });

      return keyBy(getCustomFieldDefinitions(state) || [], 'id');
    },

    useCustomFieldGroupDefinitions(): RemoteData<Record<
      number,
      CustomFieldGroupDefinition
    > | null> {
      const definitions = useSelector(getCustomFieldGroupDefinitions);
      return useMemo(
        () => rd.of(definitions ? keyBy(definitions, 'id') : null),
        [definitions]
      );
    },

    async ensureCustomFieldGroupDefinitions(): Promise<
      Record<number, CustomFieldGroupDefinition>
    > {
      const state: any = await waitForReduxState(
        store,
        (state: any) => state.auth?.settings?.custom_fieldgroup_definitions
      );
      return keyBy(getCustomFieldGroupDefinitions(state) ?? [], 'id');
    },

    useMetastructure(request) {
      const { customFieldDefinitions, customFieldGroupDefinitions } =
        useCustomFieldDefinitionsAndGroups();
      const bindingsRd = useBindings(
        maybe.map(request, r => r.scope.organizationId)
      );

      const fromRedux = useSelector((state: any) => {
        if (!request) {
          return null;
        }
        return extractMetastructureFields(state, request);
      });

      return useMemo(() => {
        const fields = maybe.of(fromRedux);
        const resolve = (metastructureFields: MetastructureField[]) =>
          resolveMetastructureFields(
            metastructureFields,
            customFieldDefinitions || {},
            customFieldGroupDefinitions || {}
          );

        return maybe.mapOrElse(
          maybe.combine({
            request,
            fields,
            workspaceId: resourceWorkspaceId(request)
          }),
          ({ request: req, fields: overlayFields }) =>
            rd.map(bindingsRd, bindings =>
              resolve(withWorkspaceOverlay(overlayFields, req, bindings))
            ),
          maybe.mapOrElse(
            maybe.combine({ request, fields }),
            ({ fields: baseFields }) => rd.of(resolve(baseFields)),
            rd.ofIdle()
          )
        );
      }, [
        bindingsRd,
        customFieldDefinitions,
        customFieldGroupDefinitions,
        fromRedux,
        request
      ]);
    },

    useOverlayCustomFieldDefinitionIds(request) {
      const bindingsRd = useBindings(
        maybe.map(request, r => r.scope.organizationId)
      );

      const fromRedux = useSelector((state: any) => {
        if (!request) {
          return null;
        }
        return extractMetastructureFields(state, request);
      });

      return useMemo(() => {
        const fields = maybe.of(fromRedux);
        return maybe.mapOrElse(
          maybe.combine({
            request,
            fields,
            workspaceId: resourceWorkspaceId(request)
          }),
          ({ request: req, fields: baseFields }) =>
            rd.map(
              bindingsRd,
              bindings =>
                mergedWorkspaceMetastructureForRequest(
                  baseFields,
                  req,
                  bindings
                )?.overlayCustomFieldDefinitionIds
            ),
          maybe.mapOrElse(
            maybe.combine({ request, fields }),
            () => rd.of<ReadonlySet<number> | undefined>(undefined),
            rd.ofIdle()
          )
        );
      }, [bindingsRd, fromRedux, request]);
    },

    useMetastructures(requests) {
      const { customFieldDefinitions, customFieldGroupDefinitions } =
        useCustomFieldDefinitionsAndGroups();
      const bindingsRd = useBindings(
        maybe.map(requests[0], r => r.scope.organizationId)
      );

      const fromRedux = useSelector((state: any) =>
        requests.map(request => extractMetastructureFields(state, request))
      );

      return useMemo(
        () =>
          fromRedux.map((fields, index) => {
            const request = requests[index];
            return maybe.mapOrElse(
              resourceWorkspaceId(request),
              () =>
                rd.map(bindingsRd, bindings =>
                  resolveMetastructureFields(
                    withWorkspaceOverlay(fields, request, bindings),
                    customFieldDefinitions || {},
                    customFieldGroupDefinitions || {}
                  )
                ),
              rd.of(
                resolveMetastructureFields(
                  fields,
                  customFieldDefinitions || {},
                  customFieldGroupDefinitions || {}
                )
              )
            );
          }),
        [
          bindingsRd,
          customFieldDefinitions,
          customFieldGroupDefinitions,
          fromRedux,
          requests
        ]
      );
    },

    useMetastructureAggregateRequests(request) {
      const workspaceId = resourceWorkspaceId(request);
      const bindingsRd = useBindings(
        maybe.map(request, r => r.scope.organizationId)
      );

      const fromRedux = useSelector((state: any) => {
        if (!request) {
          return [];
        }
        const scope = request.scope;
        switch (request.type) {
          case 'resource': {
            const resourceTypes = Object.values(
              state.resource_types?.data || {}
            ).filter((resourceType: any) => !resourceType.archived);
            return resourceTypes.map((resourceType: any) => ({
              type: 'resource' as const,
              subtypeId: resourceType.id as number,
              scope
            }));
          }
          case 'production': {
            const productionTypes = Object.values(
              state.production_types?.data || {}
            );
            return productionTypes.map((productionType: any) => ({
              type: 'production' as const,
              subtypeId: productionType.id,
              scope
            }));
          }
          case 'booking': {
            const bookingTypes = orderReducer(state.booking_types || {});
            return bookingTypes.map((bookingType: any) => ({
              type: 'booking' as const,
              subtypeId: bookingType.id,
              scope
            }));
          }
          default:
            return [];
        }
      });

      return useMemo(
        () =>
          maybe.mapOrElse(
            workspaceId,
            id => {
              const bindings = rd.tryGet(bindingsRd) ?? [];
              return fromRedux.filter(item =>
                isResourceTypeEnabledInWorkspace(bindings, item.subtypeId, id)
              );
            },
            fromRedux
          ),
        [bindingsRd, fromRedux, workspaceId]
      );
    },

    async ensureMetastructure(
      request: MetastructureRequest
    ): Promise<ResolvedMetastructureField[]> {
      const state: any = await waitForReduxState(store, (state: any) => {
        switch (request.type) {
          case 'resource':
            return (
              state.resource_types?.data &&
              Object.keys(state.resource_types.data).length > 0 &&
              state.auth?.settings?.custom_field_definitions &&
              state.auth?.settings?.custom_fieldgroup_definitions
            );
          case 'production':
            return (
              state.production_types?.data &&
              Object.keys(state.production_types.data).length > 0 &&
              state.auth?.settings?.custom_field_definitions &&
              state.auth?.settings?.custom_fieldgroup_definitions
            );
          case 'booking':
            return (
              state.booking_types?.data &&
              Object.keys(state.booking_types.data).length > 0 &&
              state.auth?.settings?.custom_field_definitions &&
              state.auth?.settings?.custom_fieldgroup_definitions
            );
          default:
            return true;
        }
      });

      const customFieldDefinitions = keyBy(
        getCustomFieldDefinitions(state) || [],
        'id'
      );
      const customFieldGroupDefinitions = keyBy(
        getCustomFieldGroupDefinitions(state) || [],
        'id'
      );

      const workspaceId = resourceWorkspaceId(request);
      if (
        request.type === 'resource' &&
        maybe.isPresent(workspaceId) &&
        resourceTypeBindingsService
      ) {
        const bindings = await resourceTypeBindingsService.ensureBindings(
          request.scope.organizationId
        );
        const fields = withWorkspaceOverlay(
          extractMetastructureFields(state, request),
          request,
          bindings
        );
        return resolveMetastructureFields(
          fields,
          customFieldDefinitions,
          customFieldGroupDefinitions
        );
      }

      const fields = extractMetastructureFields(state, request);

      return resolveMetastructureFields(
        fields,
        customFieldDefinitions,
        customFieldGroupDefinitions
      );
    }
  };
}
